import { BookingHour, PricingType } from "../../data";
import { getDates } from "@/lib/datetime";
import { getBookingHourFromBookingDate } from "./get_booking_hour_from_booking_date";

/**
 * If all booking hours have the same openAt and closedAt, returns that pair.
 * Otherwise returns null (hours vary by day).
 */
export function getBookableHoursWhenAllDaysSame(
  bookingHours: BookingHour[]
): { openAt: string; closedAt: string } | null {
  if (!bookingHours?.length) {
    return null;
  }

  const first = bookingHours[0];
  const openAt = first.openAt;
  const closedAt = first.closedAt;

  const allSame = bookingHours.every(
    (bh) => bh.openAt === openAt && bh.closedAt === closedAt
  );

  return allSame ? { openAt, closedAt } : null;
}

/**
 * For a date range, returns the single open/close pair if every day in the range
 * has the same bookable hours; otherwise returns null (do not show message).
 */
export function getBookableHoursForDateRange(
  bookingHours: BookingHour[],
  startDate: string,
  endDate: string
): { openAt: string; closedAt: string } | null {
  if (!bookingHours?.length) {
    return null;
  }

  const dates = getDates(startDate, endDate);
  if (dates.length === 0) {
    return null;
  }

  // Filter out dates that are not bookable or don't have booking hours
  const validDates = dates.filter((date) => {
    const bh = getBookingHourFromBookingDate(bookingHours, date);
    return bh?.openAt && bh?.closedAt;
  });

  if (validDates.length === 0) {
    return null;
  }

  const first = getBookingHourFromBookingDate(bookingHours, validDates[0]);

  if (!first?.openAt || !first?.closedAt) {
    return null;
  }

  const openAt = first.openAt;
  const closedAt = first.closedAt;

  const allSame = validDates.every((date) => {
    const bh = getBookingHourFromBookingDate(bookingHours, date);
    return bh?.openAt === openAt && bh?.closedAt === closedAt;
  });

  return allSame ? { openAt, closedAt } : null;
}

/**
 * Returns the bookable open/close pair to show for the "Daily rate includes..."
 * message: no date (all same hours), one day (that day's hours), or range (only if
 * all days in range have same hours). Returns null when nothing should be shown.
 */
export function getBookableHoursForDailyMessage(
  bookingHours: BookingHour[],
  startDate: string | undefined,
  endDate: string | undefined
): { openAt: string; closedAt: string } | null {
  if (!bookingHours?.length) {
    return null;
  }

  if (!startDate) {
    return getBookableHoursWhenAllDaysSame(bookingHours);
  }

  if (!endDate || endDate === startDate) {
    const bookingHour = getBookingHourFromBookingDate(bookingHours, startDate);
    if (bookingHour?.openAt && bookingHour?.closedAt) {
      return { openAt: bookingHour.openAt, closedAt: bookingHour.closedAt };
    }
    return null;
  }

  return getBookableHoursForDateRange(bookingHours, startDate, endDate);
}

/**
 * True when the space has both Daily and Hourly pricing (e.g. meeting room).
 * Used to scope " (opening - closing)" on quote lines to spaces that offer both booking types.
 */
export function hasBothDailyAndHourlyPricing(
  space: { pricings?: { type: string }[] } | null
): boolean {
  return hasDailyPricing(space) && hasHourlyPricing(space);
}

export function hasHourlyPricing(
  space: { pricings?: { type: string }[] } | null | undefined
): boolean {
  return !!space?.pricings?.some((p) => p.type === PricingType.Hourly);
}

export function hasDailyPricing(
  space: { pricings?: { type: string }[] } | null | undefined
): boolean {
  return !!space?.pricings?.some((p) => p.type === PricingType.Daily);
}
